import { Fragment, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronRight, Plus } from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/cn';
import { formatCurrency, formatCount, formatDateTime, formatPlainDate } from '../lib/format';
import { EmptyState, ErrorNote, LoadMore, Panel, Spinner, TenderChips } from '../components/ui';
import { RankedBars } from '../components/RankedBars';
import { CreditAccountDialog, type EditableAccount } from '../components/CreditAccountDialog';
import {
  DocumentViewer,
  ReceiptButton,
  type DocumentRequest,
} from '../components/DocumentViewer';
import { canWrite } from '../lib/mutate';

interface Settlement {
  settlementId: number;
  at: string;
  amount: string;
  newBalance: string;
  tillName: string;
  methods: string[];
  hasReceipt: boolean;
}

interface Account {
  id: number;
  name: string;
  contact: string | null;
  creditPeriodDays: number;
  creditLimit: string;
  currentCredit: string;
  utilisation: string | null;
  outstanding: string;
  overdueCount: number;
  oldestDueDate: string | null;
}

interface Summary {
  totals: {
    receivable: string;
    payable: string;
    customersOverLimit: number;
    overdueAccounts: number;
  };
  ageing: { label: string; amount: string; count: number }[];
}

export function Credit(): React.JSX.Element {
  const { activeStore } = useAuth();
  const storeId = activeStore?.storeId;
  const [side, setSide] = useState<'customers' | 'suppliers'>('customers');
  const [editing, setEditing] = useState<EditableAccount | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [viewing, setViewing] = useState<DocumentRequest | null>(null);
  const timeZone = activeStore?.timezone ?? 'Europe/London';

  const editable = canWrite(activeStore?.role);

  const { data: summary } = useQuery({
    queryKey: ['credit-summary', storeId],
    queryFn: () => api.get<Summary>(`/stores/${storeId}/credit/summary`),
    enabled: Boolean(storeId),
  });

  const { data, error, isPending } = useQuery({
    queryKey: ['credit', storeId, side],
    queryFn: () =>
      api.get<{ customers?: Account[]; suppliers?: Account[] }>(
        `/stores/${storeId}/credit/${side}`,
      ),
    enabled: Boolean(storeId),
  });

  const accounts = data?.customers ?? data?.suppliers ?? [];

  return (
    <>
      <header className="mb-4">
        <p className="qs-eyebrow">Credit</p>
        <h1 className="text-lg font-medium tracking-tight">Accounts</h1>
      </header>

      {summary && (
        <div className="mb-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Stat label="Owed to the shop" value={formatCurrency(summary.totals.receivable)} />
          <Stat label="Owed by the shop" value={formatCurrency(summary.totals.payable)} />
          <Stat
            label="Accounts overdue"
            value={formatCount(summary.totals.overdueAccounts)}
            warn={summary.totals.overdueAccounts > 0}
          />
          <Stat
            label="Over credit limit"
            value={formatCount(summary.totals.customersOverLimit)}
            warn={summary.totals.customersOverLimit > 0}
          />
        </div>
      )}

      {summary && (
        <Panel title="Receivables by age" className="mb-3">
          <RankedBars
            caption="Outstanding receivables grouped by days past due"
            rows={summary.ageing.map((bucket) => ({
              label: bucket.label,
              amount: bucket.amount,
              detail: `${bucket.count}`,
            }))}
            emptyMessage="Nothing outstanding."
          />
        </Panel>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1" role="group" aria-label="Account type">
          {(['customers', 'suppliers'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setSide(option)}
              aria-pressed={side === option}
              className={cn(
                'rounded border px-2.5 py-1 text-xs capitalize',
                side === option
                  ? 'border-accent bg-accent-soft font-medium text-accent'
                  : 'border-rule text-ink-muted hover:border-rule-strong hover:text-ink',
              )}
            >
              {option}
            </button>
          ))}
        </div>

        {editable && (
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="ml-auto flex items-center gap-1.5 rounded bg-accent px-2.5 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover"
          >
            <Plus size={14} aria-hidden />
            Add {side === 'customers' ? 'customer' : 'supplier'}
          </button>
        )}
      </div>

      {error && <ErrorNote error={error} />}

      <Panel>
        {isPending ? (
          <Spinner label="Loading accounts…" />
        ) : accounts.length === 0 ? (
          <EmptyState>No {side} on credit.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <caption className="sr-only">Credit {side}</caption>
              <thead>
                <tr className="border-b border-rule">
                  <th className="qs-eyebrow py-2 pl-3 text-left">Name</th>
                  <th className="qs-eyebrow hidden py-2 text-left lg:table-cell">Contact</th>
                  {/* Balance goes with the other secondary columns on a phone.
                      Four money columns cannot fit 360px, and of the two shown
                      here Outstanding is the one that is acted on, and for most
                      accounts the two figures are the same number twice. */}
                  <th className="qs-eyebrow hidden py-2 text-right sm:table-cell">Balance</th>
                  <th className="qs-eyebrow hidden py-2 text-right md:table-cell">Limit</th>
                  <th className="qs-eyebrow hidden py-2 text-right md:table-cell">Used</th>
                  {/* pr-3: without it the right-aligned figure butts straight
                      against the next column and the headers read as one word. */}
                  <th className="qs-eyebrow py-2 pr-3 text-right">Outstanding</th>
                  <th className="qs-eyebrow hidden py-2 pr-3 text-left lg:table-cell">Oldest due</th>
                  {editable && (
                    <th className="py-2 pr-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const utilisation = account.utilisation ? Number(account.utilisation) : null;
                  const stretched = utilisation !== null && utilisation >= 90;

                  const open = expanded === account.id;

                  return (
                    <Fragment key={account.id}>
                    <tr className="border-b border-rule last:border-0">
                      <td className="py-1.5 pl-3 text-xs">
                        {/* The name is the disclosure control. A separate chevron
                            column would cost a column on a table that already
                            drops three of them below lg. */}
                        {/* `wrap-anywhere`, because the name is whatever was
                            typed at the till. A 40-character account name with
                            no spaces in it is one unbreakable word, and it set
                            this column's minimum width — which the table then
                            honoured by growing past the screen and taking the
                            page's header and bottom bar sideways with it. */}
                        <button
                          type="button"
                          onClick={() => setExpanded(open ? null : account.id)}
                          aria-expanded={open}
                          className="flex w-full items-center gap-1 text-left wrap-anywhere hover:text-accent"
                        >
                          <ChevronRight
                            size={11}
                            className={cn(
                              'shrink-0 text-ink-faint transition-transform',
                              open && 'rotate-90',
                            )}
                            aria-hidden
                          />
                          {account.name}
                        </button>
                      </td>
                      <td className="qs-figure hidden py-1.5 text-xs text-ink-muted lg:table-cell">
                        {account.contact ?? '—'}
                      </td>
                      <td className="qs-figure hidden py-1.5 text-right text-xs sm:table-cell">
                        {formatCurrency(account.currentCredit)}
                      </td>
                      <td className="qs-figure hidden py-1.5 text-right text-xs text-ink-muted md:table-cell">
                        {formatCurrency(account.creditLimit)}
                      </td>
                      <td
                        className={cn(
                          'qs-figure hidden py-1.5 text-right text-xs md:table-cell',
                          stretched && 'text-down',
                        )}
                      >
                        {utilisation === null ? '—' : `${utilisation.toFixed(0)}%`}
                      </td>
                      <td className="qs-figure py-1.5 pr-3 text-right text-xs whitespace-nowrap">
                        {formatCurrency(account.outstanding)}
                      </td>
                      <td className="hidden py-1.5 pr-3 text-xs lg:table-cell">
                        {account.oldestDueDate ? (
                          <span className={account.overdueCount > 0 ? 'text-down' : undefined}>
                            {account.overdueCount > 0 && <span aria-hidden>▼ </span>}
                            {formatPlainDate(account.oldestDueDate)}
                          </span>
                        ) : (
                          <span className="text-ink-faint">—</span>
                        )}
                      </td>
                      {editable && (
                        <td className="py-1.5 pr-3 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              setEditing({
                                id: account.id,
                                name: account.name,
                                contact: account.contact,
                                creditPeriodDays: account.creditPeriodDays,
                                creditLimit: account.creditLimit,
                                currentCredit: account.currentCredit,
                              });
                              setDialogOpen(true);
                            }}
                            className="rounded border border-rule px-2 py-0.5 text-2xs text-ink-muted hover:border-accent hover:text-accent"
                          >
                            Edit
                          </button>
                        </td>
                      )}
                    </tr>

                    {open && storeId && (
                      <tr>
                        <td colSpan={editable ? 8 : 7} className="bg-sunken px-3 py-2">
                          <Settlements
                            storeId={storeId}
                            side={side}
                            accountId={account.id}
                            accountName={account.name}
                            timeZone={timeZone}
                            onView={setViewing}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Mounted only while it is open, so a rejected save cannot leave its
          message sitting over the next account's form. */}
      {storeId && dialogOpen && (
        <CreditAccountDialog
          storeId={storeId}
          side={side}
          account={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      )}

      <DocumentViewer request={viewing} onClose={() => setViewing(null)} />
    </>
  );
}

/**
 * What an account has paid off, and the receipt for each payment.
 *
 * This is the only place a credit settlement receipt can be reached from. A
 * settlement is not a sale, a return or a cash movement, so it appears on none
 * of the other pages, and `CreditSettlement` has no list of its own — but it is
 * always *about* one account, so under that account is where it belongs.
 *
 * Fetched on expand rather than with the account list: most accounts are never
 * opened, and eagerly loading settlements for every row would be one query per
 * account for a table that routinely runs to dozens.
 */
function Settlements({
  storeId,
  side,
  accountId,
  accountName,
  timeZone,
  onView,
}: {
  storeId: string;
  side: 'customers' | 'suppliers';
  accountId: number;
  accountName: string;
  timeZone: string;
  onView: (request: DocumentRequest) => void;
}): React.JSX.Element {
  const { data, error, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['settlements', storeId, side, accountId],
      queryFn: ({ pageParam }) =>
        api.get<{ settlements: Settlement[]; nextCursor: string | null }>(
          `/stores/${storeId}/credit/${side}/${accountId}/settlements?limit=25` +
            `${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
        ),
      initialPageParam: '',
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    });

  if (isPending) return <p className="py-2 text-2xs text-ink-faint">Loading payments…</p>;
  if (error) return <ErrorNote error={error} />;

  const settlements = data?.pages.flatMap((page) => page.settlements) ?? [];

  if (settlements.length === 0) {
    return (
      <p className="py-2 text-2xs text-ink-faint">
        {accountName} has made no payments against this account.
      </p>
    );
  }

  const total = settlements.reduce((sum, row) => sum + Number(row.amount), 0);

  return (
    <div>
      {/* The count and total up front: an account that has been paying for two
          years has dozens of these, and "how much has this customer paid?" is
          the question the list is usually being opened to answer. */}
      {/* "so far" while more can be fetched: a running total over a partial
          list must not be read as the account's lifetime total. */}
      <p className="qs-eyebrow mb-1.5">
        {settlements.length} payment{settlements.length === 1 ? '' : 's'} ·{' '}
        <span className="qs-figure normal-case">{formatCurrency(total.toFixed(2))}</span>
        {hasNextPage ? ' so far' : ' in total'}
      </p>

      {/* Scrolls rather than growing without bound. An expanded row that pushes
          every other account off the screen makes the table unusable for the
          thing it is actually for, which is comparing accounts. */}
      <div className="max-h-56 overflow-y-auto rounded border border-rule bg-surface">
      <table className="w-full">
        <caption className="sr-only">Settlements by {accountName}</caption>
        <thead className="sticky top-0 bg-surface">
          <tr>
            <th className="qs-eyebrow py-1 pl-2 text-left">When</th>
            <th className="qs-eyebrow hidden py-1 text-left sm:table-cell">How</th>
            <th className="qs-eyebrow hidden py-1 text-left md:table-cell">Till</th>
            <th className="qs-eyebrow py-1 pr-3 text-right">Paid</th>
            <th className="qs-eyebrow py-1 pr-3 text-right">Balance after</th>
            <th className="py-1 pr-2">
              <span className="sr-only">Receipt</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {settlements.map((settlement) => (
            <tr key={settlement.settlementId} className="border-t border-rule">
              <td className="qs-figure py-1 pl-2 text-2xs whitespace-nowrap">
                {formatDateTime(settlement.at, timeZone)}
              </td>
              <td className="hidden py-1 sm:table-cell">
                <TenderChips methods={settlement.methods} />
              </td>
              <td className="hidden py-1 text-2xs text-ink-muted md:table-cell">
                {settlement.tillName}
              </td>
              <td className="qs-figure py-1 pr-3 text-right text-2xs text-up">
                {formatCurrency(settlement.amount)}
              </td>
              <td className="qs-figure py-1 pr-3 text-right text-2xs text-ink-muted">
                {formatCurrency(settlement.newBalance)}
              </td>
              <td className="py-1 pr-2 text-right">
                {settlement.hasReceipt ? (
                  <ReceiptButton
                    label={`Receipt for settlement #${settlement.settlementId}`}
                    onOpen={() =>
                      onView({
                        path: `/stores/${storeId}/documents/settlement/${settlement.settlementId}`,
                        title: `${accountName} — payment #${settlement.settlementId}`,
                        formats: ['text'],
                      })
                    }
                  />
                ) : (
                  // Known in advance from the row itself, so the button is not
                  // offered rather than offered and then answering 404.
                  <span className="text-2xs text-ink-faint" title="Never uploaded from the till">
                    —
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <LoadMore
        shown={settlements.length}
        hasMore={Boolean(hasNextPage)}
        isLoading={isFetchingNextPage}
        onLoad={() => void fetchNextPage()}
        noun="payments shown"
      />
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  warn = false,
}: {
  label: string;
  value: string;
  warn?: boolean;
}): React.JSX.Element {
  return (
    <div className={cn('qs-panel p-3', warn && 'border-warn/30 bg-warn-soft')}>
      <p className={cn('qs-eyebrow', warn && 'text-warn')}>{label}</p>
      <p className={cn('qs-figure mt-1 text-lg leading-none font-medium', warn && 'text-warn')}>
        {value}
      </p>
    </div>
  );
}
